// Service Worker Registration and PWA utilities

export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          console.log('✅ ServiceWorker registered:', registration.scope);
          
          // Check for updates every hour
          setInterval(() => {
            registration.update();
          }, 60 * 60 * 1000);
          
          // Handle updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version available
                if (window.confirm('New version available! Reload to update?')) {
                  newWorker.postMessage({ action: 'skipWaiting' });
                  window.location.reload();
                }
              }
            });
          });
        })
        .catch((error) => {
          console.error('❌ ServiceWorker registration failed:', error);
        });
    });
    
    // Reload page when new service worker takes control
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });
  }
};

export const unregisterServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error('❌ ServiceWorker unregistration failed:', error);
      });
  }
};

// Check if app is installed
export const isAppInstalled = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
};

// Check if app is installable
export const canInstallApp = () => {
  return 'BeforeInstallPromptEvent' in window;
};

// PWA Install Prompt
let deferredPrompt = null;

export const setupInstallPrompt = (onInstallable) => {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    
    if (onInstallable) {
      onInstallable(true);
    }
  });
  
  window.addEventListener('appinstalled', () => {
    console.log('✅ PWA installed successfully');
    deferredPrompt = null;
    
    if (onInstallable) {
      onInstallable(false);
    }
  });
};

export const showInstallPrompt = async () => {
  if (!deferredPrompt) {
    return { outcome: 'dismissed', reason: 'No prompt available' };
  }
  
  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  
  console.log(`User response: ${outcome}`);
  deferredPrompt = null;
  
  return { outcome };
};

// Check online/offline status
export const setupNetworkListener = (onStatusChange) => {
  const updateOnlineStatus = () => {
    const isOnline = navigator.onLine;
    onStatusChange(isOnline);
  };
  
  window.addEventListener('online', updateOnlineStatus);
  window.addEventListener('offline', updateOnlineStatus);
  
  // Initial check
  updateOnlineStatus();
  
  // Return cleanup function
  return () => {
    window.removeEventListener('online', updateOnlineStatus);
    window.removeEventListener('offline', updateOnlineStatus);
  };
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }
  
  return Notification.permission;
};

// Show notification
export const showNotification = (title, options = {}) => {
  if ('serviceWorker' in navigator && 'PushManager' in window) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.showNotification(title, {
        icon: '/pwa-icons/icon-192x192.png',
        badge: '/pwa-icons/icon-72x72.png',
        vibrate: [200, 100, 200],
        ...options
      });
    });
  } else if (Notification.permission === 'granted') {
    new Notification(title, options);
  }
};

// Get install instructions based on device/browser
export const getInstallInstructions = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
  const isAndroid = /android/.test(userAgent);
  const isChrome = /chrome/.test(userAgent);
  
  if (isIOS && isSafari) {
    return {
      platform: 'iOS Safari',
      steps: [
        'Tap the Share button (square with arrow)',
        'Scroll down and tap "Add to Home Screen"',
        'Tap "Add" in the top right corner'
      ]
    };
  }
  
  if (isAndroid && isChrome) {
    return {
      platform: 'Android Chrome',
      steps: [
        'Tap the menu button (three dots)',
        'Tap "Add to Home screen"',
        'Tap "Add" to confirm'
      ]
    };
  }
  
  return {
    platform: 'Desktop',
    steps: [
      'Click the install icon in the address bar',
      'Or use the app menu to install',
      'Follow the prompts to install'
    ]
  };
};
